import React from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { formatIndianNumber } from '../../utils/indianNumberFormat';

interface LedgerSummaryCardProps {
  totalCredit: number;
  totalDebit: number;
  totalBalance: number;
}

const LedgerSummaryCard: React.FC<LedgerSummaryCardProps> = ({
  totalCredit,
  totalDebit,
  totalBalance,
}) => {
  const { theme, isDark } = useTheme();
  const navigation = useNavigation<any>();

  const formatCurrency = (amount: number) => {
    const sign = amount < 0 ? '-' : '';
    const absAmount = Math.abs(amount);
    return `${sign}${formatIndianNumber(absAmount)}`;
  };

  const handlePress = () => {
    navigation.navigate('Ledger');
  };

  return (
    <TouchableOpacity 
      style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: theme.success + '20' }]}>
          <Ionicons name="wallet-outline" size={24} color={theme.success} />
        </View>
        <ThemedText style={[styles.title, { color: theme.text }]}>
          Account Summary
        </ThemedText>
      </View>

      <View style={styles.statsContainer}>
        <View style={styles.statItem}>
          <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
            Total Credit
          </ThemedText>
          <ThemedText style={[styles.statValue, { color: theme.success }]}>
            {formatCurrency(totalCredit)}
          </ThemedText>
        </View>

        <View style={[styles.divider, { backgroundColor: theme.border }]} />

        <View style={styles.statItem}>
          <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
            Total Debit
          </ThemedText>
          <ThemedText style={[styles.statValue, { color: theme.error }]}>
            {formatCurrency(totalDebit)}
          </ThemedText>
        </View>

        <View style={[styles.divider, { backgroundColor: theme.border }]} />

        <View style={styles.statItem}>
          <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
            Balance
          </ThemedText>
          <ThemedText style={[styles.statValue, { color: totalBalance >= 0 ? theme.success : theme.error }]}>
            {formatCurrency(totalBalance)}
          </ThemedText>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 12,
    marginBottom: 4,
    textAlign: 'center',
  },
  statValue: {
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
    flexWrap: 'wrap',
  },
  divider: {
    width: 1,
    height: 40,
    marginHorizontal: 8,
  },
});

export default LedgerSummaryCard;
